import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { PmvActivityTimeline } from '@/components/pmv/PmvActivityTimeline';
import pmvService from '@/services/pmv.service';
import pmvHireService, { formatHireMoney } from '@/services/pmv-hire.service';
import { useProjects } from '@/hooks/useProjects';
import {
  buildPmvProfileIdToLabelMap,
  buildProjectIdToLabelMap,
  resolvePmvProfileDisplayFromMap,
  resolveProjectDisplayFromMap,
} from '@/utils/pmvDisplay';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const statusColor: Record<string, string> = {
  DRAFT: 'default',
  SUBMITTED: 'processing',
  CERTIFIED: 'success',
  RETURNED: 'warning',
  VOID: 'error',
};

const fmtHours = (m: number) => `${(m / 60).toFixed(1)} h`;

const PmvDailyLogWorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const screens = useBreakpoint();
  const compactDefault = !screens.md;
  const [viewMode, setViewMode] = useState<'field' | 'full'>('field');
  const [timeForm] = Form.useForm();
  const [outputForm] = Form.useForm();
  const [meterForm] = Form.useForm();
  const [openingForm] = Form.useForm();
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnForm] = Form.useForm();
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const profilesQ = useQuery({
    queryKey: ['pmv-profiles-labels'],
    queryFn: () => pmvService.listProfiles({ pageSize: 200 }),
  });
  const profileLabels = useMemo(
    () => buildPmvProfileIdToLabelMap(profilesQ.data?.data ?? []),
    [profilesQ.data?.data]
  );

  const effectiveMode = viewMode === 'field' || compactDefault ? 'field' : 'full';
  const stacked = effectiveMode === 'field';

  const logQ = useQuery({
    queryKey: ['pmv-daily-log', id],
    queryFn: () => pmvService.getDailyLog(id!),
    enabled: Boolean(id),
  });

  const allocQ = useQuery({
    queryKey: ['pmv-allocation', logQ.data?.allocation_id],
    queryFn: () => pmvService.getAllocation(logQ.data!.allocation_id),
    enabled: Boolean(logQ.data?.allocation_id),
  });

  const analyticsQ = useQuery({
    queryKey: ['pmv-daily-log-analytics', id],
    queryFn: () => pmvService.getDailyLogAnalytics(id!),
    enabled: Boolean(id) && logQ.data?.status === 'CERTIFIED',
  });

  const sheetId = (logQ.data as { sheet_id?: string | null } | undefined)?.sheet_id;
  const sheetQ = useQuery({
    queryKey: ['pmv-daily-log-sheet-ref', sheetId],
    queryFn: () =>
      import('@/services/pmv-field-ops.service').then((m) => m.default.getSheet(sheetId!)),
    enabled: Boolean(sheetId),
  });

  const hireQ = useQuery({
    queryKey: ['pmv-hire-preview-log', logQ.data?.pmv_profile_id, logQ.data?.operating_date],
    queryFn: async () => {
      const profileId = logQ.data!.pmv_profile_id;
      const agreements = await pmvHireService.listAgreements({
        pmv_profile_id: profileId,
        limit: 5,
      });
      const onHire =
        agreements.items.find((a) => a.commercial_status === 'ON_HIRE') || agreements.items[0];
      if (!onHire) return null;
      const rate = await pmvHireService.resolveEffectiveRate(
        onHire.id,
        logQ.data!.operating_date
      );
      return { agreement: onHire, rate };
    },
    enabled: Boolean(logQ.data?.pmv_profile_id),
  });

  const log = logQ.data;
  const editable = log && ['DRAFT', 'RETURNED'].includes(log.status);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['pmv-daily-log', id] });
    void qc.invalidateQueries({ queryKey: ['pmv-daily-log-analytics', id] });
  };

  const submitMut = useMutation({
    mutationFn: () => pmvService.submitDailyLog(id!),
    onSuccess: () => { message.success('Submitted'); invalidate(); },
    onError: (e: Error) => message.error(e.message),
  });

  const certifyMut = useMutation({
    mutationFn: () => pmvService.certifyDailyLog(id!),
    onSuccess: () => { message.success('Certified'); invalidate(); },
    onError: (e: Error) => message.error(e.message),
  });

  const timeMut = useMutation({
    mutationFn: (v: Record<string, unknown>) => pmvService.createTimeEntry(id!, v),
    onSuccess: () => { message.success('Time entry added'); timeForm.resetFields(); invalidate(); },
    onError: (e: Error) => message.error(e.message),
  });

  const outputMut = useMutation({
    mutationFn: (v: Record<string, unknown>) => pmvService.createOutputEntry(id!, v),
    onSuccess: () => { message.success('Output added'); outputForm.resetFields(); invalidate(); },
    onError: (e: Error) => message.error(e.message),
  });

  const meterMut = useMutation({
    mutationFn: (v: { closing_meter_value: number }) => pmvService.patchDailyLog(id!, v),
    onSuccess: () => { message.success('Closing meter saved'); meterForm.resetFields(); invalidate(); },
    onError: (e: Error) => message.error(e.message),
  });

  const openingMut = useMutation({
    mutationFn: (v: { opening_meter_value: number }) => pmvService.patchDailyLog(id!, v),
    onSuccess: () => { message.success('Opening meter saved'); openingForm.resetFields(); invalidate(); },
    onError: (e: Error) => message.error(e.message),
  });

  const returnMut = useMutation({
    mutationFn: (reason: string) => pmvService.returnDailyLog(id!, reason),
    onSuccess: () => {
      message.success('Returned for correction');
      setReturnOpen(false);
      returnForm.resetFields();
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const voidMut = useMutation({
    mutationFn: (reason: string) => pmvService.voidDailyLog(id!, reason),
    onSuccess: () => {
      message.success('Daily log voided');
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const confirmVoid = () => {
    let reason = '';
    Modal.confirm({
      title: 'Void daily log?',
      content: (
        <Input.TextArea
          rows={3}
          placeholder="Reason for void"
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okText: 'Void',
      okButtonProps: { danger: true },
      onOk: () => {
        const trimmed = reason.trim();
        if (!trimmed) {
          message.error('Reason is required');
          return Promise.reject(new Error('Reason required'));
        }
        return voidMut.mutateAsync(trimmed);
      },
    });
  };

  const opening = log?.openingMeter?.reading_value;
  const closing = log?.closingMeter?.reading_value;
  const meterDelta = opening != null && closing != null ? closing - opening : null;

  const primaryCta = useMemo(() => {
    if (!log) return null;
    if (log.status === 'VOID') {
      return <Tag color="error">Voided</Tag>;
    }
    if (log.status === 'DRAFT' || log.status === 'RETURNED') {
      return (
        <Space wrap>
          <Button type="primary" loading={submitMut.isPending} onClick={() => submitMut.mutate()}>
            Submit for certification
          </Button>
          <Button danger loading={voidMut.isPending} onClick={confirmVoid}>
            Void
          </Button>
        </Space>
      );
    }
    if (log.status === 'SUBMITTED') {
      return (
        <Space wrap>
          <Button onClick={() => setReturnOpen(true)}>Return for correction</Button>
          <Button type="primary" loading={certifyMut.isPending} onClick={() => certifyMut.mutate()}>
            Certify
          </Button>
          <Button danger loading={voidMut.isPending} onClick={confirmVoid}>
            Void
          </Button>
        </Space>
      );
    }
    return <Tag color="success">Certified — immutable</Tag>;
  }, [log, submitMut.isPending, certifyMut.isPending, voidMut.isPending]);

  const timeEntryForm = (
    <Form
      form={timeForm}
      layout={stacked ? 'vertical' : 'inline'}
      onFinish={(v) => timeMut.mutate(v)}
      style={stacked ? { maxWidth: 480 } : undefined}
    >
      <Form.Item name="start_time" label={stacked ? 'Start' : undefined} rules={[{ required: true }]}>
        <Input placeholder="07:00:00" style={{ width: stacked ? '100%' : 100 }} />
      </Form.Item>
      <Form.Item name="end_time" label={stacked ? 'End' : undefined} rules={[{ required: true }]}>
        <Input placeholder="09:15:00" style={{ width: stacked ? '100%' : 100 }} />
      </Form.Item>
      <Form.Item name="activity_type" label={stacked ? 'Activity' : undefined} rules={[{ required: true }]}>
        <Select
          style={{ width: stacked ? '100%' : 140 }}
          options={['WORKING', 'IDLE', 'STANDBY', 'BREAKDOWN', 'OTHER'].map((v) => ({ value: v, label: v }))}
        />
      </Form.Item>
      <Form.Item name="reason_code" label={stacked ? 'Reason' : undefined}>
        <Input placeholder="Reason code" style={{ width: stacked ? '100%' : 140 }} />
      </Form.Item>
      <Form.Item label={stacked ? ' ' : undefined} colon={false}>
        <Button type="primary" htmlType="submit" loading={timeMut.isPending} block={stacked}>
          Add activity
        </Button>
      </Form.Item>
    </Form>
  );

  const certificationPanel = log ? (
    <Descriptions bordered size="small" column={1}>
      <Descriptions.Item label="Status"><Tag color={statusColor[log.status]}>{log.status}</Tag></Descriptions.Item>
      <Descriptions.Item label="Submitted at">{log.submitted_at ?? '—'}</Descriptions.Item>
      <Descriptions.Item label="Certified at">{log.certified_at ?? '—'}</Descriptions.Item>
      {log.return_reason && (
        <Descriptions.Item label="Return reason">{log.return_reason}</Descriptions.Item>
      )}
    </Descriptions>
  ) : null;

  const fieldEntryPanel = log ? (
    <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 640, paddingBottom: stacked ? 72 : 0 }}>
      {editable && opening == null && (
        <Card size="small" title="Opening meter">
          <Form form={openingForm} layout="vertical" onFinish={(v) => openingMut.mutate(v)}>
            <Form.Item name="opening_meter_value" label="Opening reading" rules={[{ required: true }]}>
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={openingMut.isPending} block>
              Save opening meter
            </Button>
          </Form>
        </Card>
      )}
      <Card size="small" title="Timeline">
        <PmvActivityTimeline entries={log.timeEntries ?? []} />
      </Card>
      {editable && (
        <Card size="small" title="Add activity">
          {timeEntryForm}
        </Card>
      )}
      {editable && (
        <Card size="small" title="Closing meter">
          <Form form={meterForm} layout="vertical" onFinish={(v) => meterMut.mutate(v)}>
            <Form.Item name="closing_meter_value" label="Closing reading" rules={[{ required: true }]}>
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={meterMut.isPending} block>
              Save closing meter
            </Button>
          </Form>
        </Card>
      )}
      {editable && (
        <Card size="small" title="Work output">
          <Form form={outputForm} layout="vertical" onFinish={(v) => outputMut.mutate(v)}>
            <Form.Item name="activity_description" label="Activity" rules={[{ required: true }]}>
              <Input placeholder="Excavation / haul" />
            </Form.Item>
            <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="uom" label="UOM" rules={[{ required: true }]}>
              <Input placeholder="m³ / t / km" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={outputMut.isPending} block>
              Add output
            </Button>
          </Form>
        </Card>
      )}
      <Card size="small" title="Attachments">
        <EntityAttachmentsCard entityType="PMV_DAILY_LOG" entityId={id} title="Daily log evidence" />
      </Card>
      <Card size="small" title="Certification">
        {certificationPanel}
      </Card>
    </Space>
  ) : null;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Daily Equipment Log"
          documentNumber={log?.log_number ?? ''}
          statusTag={
            log ? (
              <Space size={4} wrap>
                <Tag color={statusColor[log.status]}>{log.status}</Tag>
                {log.status === 'CERTIFIED' ? <Tag color="success">Certified — immutable</Tag> : null}
              </Space>
            ) : undefined
          }
          extra={
            <Space wrap>
              {!compactDefault && (
                <Segmented
                  value={viewMode}
                  onChange={(v) => setViewMode(v as 'field' | 'full')}
                  options={[
                    { label: 'Field entry', value: 'field' },
                    { label: 'Full workspace', value: 'full' },
                  ]}
                />
              )}
              {!stacked && primaryCta}
            </Space>
          }
        />
      }
    >
      {log && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {sheetId ? (
            <Alert
              type="info"
              showIcon
              message={
                <span>
                  Captured via Daily Log Sheet{' '}
                  <Link to={`/asset/pmv/daily-log-sheets/${sheetId}`}>
                    {sheetQ.data?.sheet_number || sheetId.slice(0, 8)}
                  </Link>
                  {sheetQ.data?.status ? ` (${sheetQ.data.status})` : ''}
                </span>
              }
            />
          ) : null}
          {hireQ.data?.agreement ? (
            <Card size="small" title="Hired equipment — commercial interpretation (preview)">
              <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
                <Descriptions.Item label="Agreement">
                  <Link to={`/asset/pmv/hire/agreements/${hireQ.data.agreement.id}`}>
                    {hireQ.data.agreement.agreement_number}
                  </Link>
                </Descriptions.Item>
                <Descriptions.Item label="Hire state">
                  <Tag>{hireQ.data.agreement.commercial_status}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Rate">
                  {(() => {
                    const eff = hireQ.data.rate;
                    const rateObj =
                      eff && typeof eff === 'object' && 'rate' in eff
                        ? (eff as { rate: { hourly_rate?: string | number | null; daily_rate?: string | number | null; rate_basis?: string; currency?: string } | null; available?: boolean }).rate
                        : (eff as { hourly_rate?: string | number | null; rate_basis?: string; currency?: string } | null);
                    const available =
                      eff && typeof eff === 'object' && 'available' in eff
                        ? !!(eff as { available?: boolean }).available
                        : !!rateObj;
                    if (!available || !rateObj) return 'Unavailable';
                    const raw =
                      rateObj.rate_basis === 'DAILY'
                        ? (rateObj as { daily_rate?: string | number | null }).daily_rate
                        : rateObj.hourly_rate;
                    return raw == null || raw === ''
                      ? 'Unavailable'
                      : formatHireMoney(raw, rateObj.currency);
                  })()}
                </Descriptions.Item>
              </Descriptions>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Preview from hire backend only — not an FE money engine. Working hours ≠ billable hours.
              </Text>
            </Card>
          ) : null}
          <Card>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="Operating date" value={log.operating_date} />
                <Text type="secondary">Shift: {log.shift_code}</Text>
              </Col>
              <Col xs={12} sm={6} md={4}>
                <Statistic title="Opening meter" value={opening ?? '—'} precision={1} />
              </Col>
              <Col xs={12} sm={6} md={4}>
                <Statistic title="Closing meter" value={closing ?? '—'} precision={1} />
              </Col>
              <Col xs={12} sm={6} md={4}>
                <Statistic title="Meter delta" value={meterDelta ?? '—'} precision={1} />
              </Col>
              <Col xs={12} sm={6} md={6}>
                <Statistic
                  title="Working utilization"
                  value={analyticsQ.data?.utilization.workingUtilization ?? '—'}
                  suffix={analyticsQ.data?.utilization.workingUtilization != null ? '%' : ''}
                />
                {analyticsQ.data && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    denom: {analyticsQ.data.utilization.denominator}
                  </Text>
                )}
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
              <Col xs={12} sm={6}><Statistic title="Working" value={fmtHours(log.working_minutes)} /></Col>
              <Col xs={12} sm={6}><Statistic title="Idle" value={fmtHours(log.idle_minutes)} /></Col>
              <Col xs={12} sm={6}><Statistic title="Standby" value={fmtHours(log.standby_minutes)} /></Col>
              <Col xs={12} sm={6}><Statistic title="Breakdown" value={fmtHours(log.breakdown_minutes)} /></Col>
            </Row>
          </Card>

          {effectiveMode === 'field' ? (
            fieldEntryPanel
          ) : (
            <Tabs
              items={[
                {
                  key: 'overview',
                  label: 'Overview',
                  children: (
                    <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
                      <Descriptions.Item label="Project">
                        {resolveProjectDisplayFromMap(projectLabels, log.project_id)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Allocation">
                        {allocQ.data?.allocation_number ?? '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="PMV unit">
                        <Button type="link" onClick={() => navigate(`/asset/pmv/${log.pmv_profile_id}`)}>
                          {resolvePmvProfileDisplayFromMap(profileLabels, log.pmv_profile_id)}
                        </Button>
                      </Descriptions.Item>
                      <Descriptions.Item label="Available">{fmtHours(log.available_minutes)}</Descriptions.Item>
                      <Descriptions.Item label="Work description" span={2}>{log.work_description ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="Remarks" span={2}>{log.remarks ?? '—'}</Descriptions.Item>
                    </Descriptions>
                  ),
                },
                {
                  key: 'time',
                  label: 'Time & Activity',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <PmvActivityTimeline entries={log.timeEntries ?? []} />
                      <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={log.timeEntries ?? []}
                        columns={[
                          { title: 'Start', dataIndex: 'start_time' },
                          { title: 'End', dataIndex: 'end_time' },
                          { title: 'Type', dataIndex: 'activity_type', render: (v) => <Tag>{v}</Tag> },
                          { title: 'Duration', dataIndex: 'duration_minutes', render: (v) => `${v} min` },
                          { title: 'Reason', dataIndex: 'reason_code' },
                        ]}
                      />
                      {editable && (
                        <Card size="small" title="Add activity">
                          {timeEntryForm}
                        </Card>
                      )}
                    </Space>
                  ),
                },
                {
                  key: 'meters',
                  label: 'Meters',
                  children: (
                    <Space direction="vertical">
                      {editable && opening == null && (
                        <Form form={openingForm} layout="inline" onFinish={(v) => openingMut.mutate(v)}>
                          <Form.Item name="opening_meter_value" label="Opening" rules={[{ required: true }]}>
                            <InputNumber min={0} step={0.1} />
                          </Form.Item>
                          <Button type="primary" htmlType="submit" loading={openingMut.isPending}>Save opening</Button>
                        </Form>
                      )}
                      {editable ? (
                        <Form form={meterForm} layout="inline" onFinish={(v) => meterMut.mutate(v)}>
                          <Form.Item name="closing_meter_value" label="Closing meter" rules={[{ required: true }]}>
                            <InputNumber min={0} step={0.1} />
                          </Form.Item>
                          <Button type="primary" htmlType="submit" loading={meterMut.isPending}>Save closing meter</Button>
                        </Form>
                      ) : (
                        <Descriptions size="small">
                          <Descriptions.Item label="Opening">{opening ?? '—'}</Descriptions.Item>
                          <Descriptions.Item label="Closing">{closing ?? '—'}</Descriptions.Item>
                          <Descriptions.Item label="Reconciliation">{analyticsQ.data?.reconciliation.state ?? '—'}</Descriptions.Item>
                        </Descriptions>
                      )}
                    </Space>
                  ),
                },
                {
                  key: 'output',
                  label: 'Work Output',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Table
                        rowKey="id"
                        size="small"
                        dataSource={log.outputs ?? []}
                        pagination={false}
                        columns={[
                          { title: 'Activity', dataIndex: 'activity_description' },
                          { title: 'Qty', dataIndex: 'quantity' },
                          { title: 'UOM', dataIndex: 'uom' },
                        ]}
                      />
                      {editable && (
                        <Form form={outputForm} layout="inline" onFinish={(v) => outputMut.mutate(v)}>
                          <Form.Item name="activity_description" rules={[{ required: true }]}>
                            <Input placeholder="Activity" style={{ width: 160 }} />
                          </Form.Item>
                          <Form.Item name="quantity" rules={[{ required: true }]}>
                            <InputNumber min={0} placeholder="Qty" />
                          </Form.Item>
                          <Form.Item name="uom" rules={[{ required: true }]}>
                            <Input placeholder="UOM" style={{ width: 80 }} />
                          </Form.Item>
                          <Button type="primary" htmlType="submit" loading={outputMut.isPending}>Add</Button>
                        </Form>
                      )}
                    </Space>
                  ),
                },
                {
                  key: 'attachments',
                  label: 'Attachments',
                  children: (
                    <EntityAttachmentsCard entityType="PMV_DAILY_LOG" entityId={id} title="Daily log evidence" />
                  ),
                },
                {
                  key: 'certification',
                  label: 'Certification',
                  children: certificationPanel,
                },
              ]}
            />
          )}

          <Button onClick={() => navigate(`/asset/pmv/${log.pmv_profile_id}`)}>
            Open PMV 360 — {resolvePmvProfileDisplayFromMap(profileLabels, log.pmv_profile_id)}
          </Button>

          {returnOpen && (
            <Card size="small" title="Return daily log for correction">
              <Form
                form={returnForm}
                layout="vertical"
                style={{ maxWidth: 640 }}
                onFinish={(v) => returnMut.mutate(v.reason as string)}
              >
                <Form.Item name="reason" label="Reason" rules={[{ required: true, min: 3 }]}>
                  <Input.TextArea rows={3} placeholder="Explain what needs correction" />
                </Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={returnMut.isPending}>
                    Return
                  </Button>
                  <Button onClick={() => setReturnOpen(false)}>Cancel</Button>
                </Space>
              </Form>
            </Card>
          )}

          {stacked && primaryCta && (
            <div
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '12px 16px',
                background: '#fff',
                borderTop: '1px solid #f0f0f0',
                zIndex: 100,
              }}
            >
              <Button type="primary" block loading={submitMut.isPending || certifyMut.isPending} onClick={() => {
                if (log.status === 'DRAFT' || log.status === 'RETURNED') submitMut.mutate();
                else if (log.status === 'SUBMITTED') certifyMut.mutate();
              }}
              disabled={log.status === 'CERTIFIED' || log.status === 'VOID'}
              >
                {log.status === 'SUBMITTED' ? 'Certify' : 'Submit for certification'}
              </Button>
            </div>
          )}
        </Space>
      )}
    </WorkspaceLayout>
  );
};

export default PmvDailyLogWorkspacePage;
